import { useMemo } from "react";

import { faCircleCheck } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, LinearProgress, Stack, Typography } from "@mui/material";
import type { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { useTranslation } from "react-i18next";
import styled, { useTheme } from "styled-components";

import type { Professional } from "api/services/admin/schemas";
import { Avatar } from "shared/atoms/Avatar/Avatar";
import { Tooltip } from "shared/atoms/Tooltip";
import { convertCentsToDollarsNumber } from "utils/convertCentsToDollars";
import { translateCurrency, translateProfession, translateProfessionalStatus } from "utils/translations";

const formatCents = (cents: number, currency: string) =>
  translateCurrency(convertCentsToDollarsNumber(cents), currency);

interface UseTeamMembersColumnsParams {
  isCurrentMonth: boolean;
}

export const useTeamMembersColumns = ({ isCurrentMonth }: UseTeamMembersColumnsParams) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const nameColumn: GridColDef<Professional> = {
    field: "name",
    headerName: t("booking.services.name"),
    flex: 1.5,
    minWidth: 180,
    renderCell: ({ row }: GridRenderCellParams<Professional>) => (
      <Stack direction="row" spacing={1} alignItems="center">
        <Avatar id={row.id} size={40} src={row.avatars?.medium || ""} name={row.name} />
        <Typography variant="subtitle2">{row.name}</Typography>
      </Stack>
    ),
  };

  const detailsColumns: GridColDef[] = useMemo(
    () => [
      nameColumn,
      {
        field: "email",
        headerName: t("form.email"),
        flex: 1,
        minWidth: 155,
      },
      {
        field: "profession",
        headerName: t("patients.medical_records.role"),
        renderCell: params => translateProfession(params.value),
        flex: 1,
        minWidth: 155,
      },
      {
        field: "status",
        headerName: t("patients.status"),
        flex: 1,
        minWidth: 155,
        renderCell: ({ row }: GridRenderCellParams<Professional>) => (
          <StatusChip className={row.status}>
            <Typography variant="label_bold">{translateProfessionalStatus(row.status)}</Typography>
          </StatusChip>
        ),
      },
    ],
    [t]
  );

  const performanceColumns: GridColDef<Professional>[] = useMemo(
    () => [
      nameColumn,
      {
        field: "email",
        headerName: t("form.email"),
      },
      {
        field: "enrollment",
        headerName: t("admin.team_members.column_enrollment"),
        flex: 1.5,
        minWidth: 200,
        sortable: false,
        valueFormatter: (_value, row) =>
          `${row.enrolled_patients}${row.enrollment_goal > 0 ? `/${row.enrollment_goal}` : ""}`,
        valueGetter: (_value, row) => row.enrolled_patients,
        renderCell: ({ row }: GridRenderCellParams<Professional>) => (
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ width: "100%" }}>
            {row.enrollment_goal > 0 && (
              <Box sx={{ flex: 1 }}>
                <StyledProgress
                  variant="determinate"
                  value={Math.min((row.enrolled_patients / row.enrollment_goal) * 100, 100)}
                  $color={
                    row.enrolled_patients >= row.enrollment_goal
                      ? theme.colors.redesign.g100
                      : theme.colors.redesign.b100
                  }
                />
              </Box>
            )}
            <Typography variant="subtitle2" sx={{ minWidth: 50, textAlign: "right", whiteSpace: "nowrap" }}>
              {row.enrolled_patients}
              {row.enrollment_goal > 0 ? `/${row.enrollment_goal}` : ""}
            </Typography>
          </Stack>
        ),
      },
      {
        field: "earnings_cents",
        headerName: t("admin.team_members.column_earnings"),
        flex: 0.8,
        minWidth: 140,
        align: "right",
        headerAlign: "right",
        valueFormatter: (value, row) => formatCents(value as number, row.currency),
        renderCell: ({ row, value }: GridRenderCellParams<Professional>) => (
          <Typography variant="subtitle2" color={value > 0 ? theme.colors.redesign.db100 : theme.colors.redesign.db30}>
            {formatCents(value as number, row.currency)}
          </Typography>
        ),
      },
      {
        field: "care_event_commission_cents",
        headerName: t("admin.team_members.column_commission"),
        flex: 0.8,
        minWidth: 150,
        align: "right",
        headerAlign: "right",
        valueFormatter: (value, row) => formatCents(value as number, row.currency),
        renderCell: ({ row, value }: GridRenderCellParams<Professional>) => (
          <Typography variant="subtitle2" color={value > 0 ? theme.colors.redesign.g100 : theme.colors.redesign.db30}>
            {formatCents(value as number, row.currency)}
          </Typography>
        ),
      },
      {
        field: "bonus_commission_cents",
        headerName: t("admin.team_members.column_bonus"),
        flex: 0.8,
        minWidth: 100,
        align: "right",
        headerAlign: "right",
        valueFormatter: (value, row) => formatCents(value as number, row.currency),
        renderCell: ({ row, value }: GridRenderCellParams<Professional>) => (
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Typography variant="subtitle2" color={value > 0 ? theme.colors.redesign.g100 : theme.colors.redesign.db30}>
              {formatCents(value as number, row.currency)}
            </Typography>
            {value > 0 && !isCurrentMonth && (
              <Tooltip title={t("admin.team_members.earned_badge")}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <FontAwesomeIcon icon={faCircleCheck} color={theme.colors.redesign.g100} />
                </Box>
              </Tooltip>
            )}
          </Stack>
        ),
      },
      {
        field: "totalEarned",
        headerName: t("admin.team_members.column_total_earned"),
        flex: 0.6,
        minWidth: 100,
        align: "right",
        headerAlign: "right",
        valueGetter: (_value, row) =>
          (row.care_event_commission_cents as number) + (row.bonus_commission_cents as number),
        valueFormatter: (value, row) => formatCents(value, row.currency),
        renderCell: ({ value, row }: GridRenderCellParams<Professional>) => (
          <Typography variant="subtitle2" color={value > 0 ? theme.colors.redesign.db100 : theme.colors.redesign.db30}>
            {formatCents(value, row.currency)}
          </Typography>
        ),
      },
    ],
    [t, theme, isCurrentMonth]
  );

  return { detailsColumns, performanceColumns };
};

const StatusChip = styled.div`
  display: flex;
  padding: 4px 8px;
  border-radius: 4px;
  background-color: ${props => props.theme.colors.redesign.g10};

  span {
    color: ${props => props.theme.colors.redesign.g100};
  }

  &.in_onboarding {
    background-color: ${props => props.theme.colors.redesign.o10};
    span {
      color: ${props => props.theme.colors.redesign.o100};
    }
  }
  &.deleted {
    background-color: ${props => props.theme.colors.redesign.r10};
    span {
      color: ${props => props.theme.colors.redesign.r100};
    }
  }
`;

const StyledProgress = styled(LinearProgress)<{ $color: string }>`
  && {
    height: 8px;
    border-radius: 4px;
    background-color: ${({ theme }) => theme.colors.redesign.db10};

    .MuiLinearProgress-bar {
      background-color: ${({ $color }) => $color};
      border-radius: 4px;
    }
  }
`;
